import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Flag, Settings2 } from 'lucide-react';
import { apiGet } from '../lib/api';
import { describeQuery, listFlags, queryToSearchParams, searchParamsToQuery, type FindQuery, type FlagListItem } from '../lib/flags';
import { useFlagStore, useFlagStoreVersion } from '../contexts/FlagStateContext';
import { useToast } from '../contexts/ToastContext';
import { FlagFilters, type Option } from '../components/flags/FlagFilters';
import { FlagList, sourceLine, sourceUrl } from '../components/flags/FlagList';
import { FlagPopover } from '../components/flags/FlagPopover';

/**
 * Find flags (#316): every flag by type, status, project, series, person,
 * date and text. The filters live in the URL so a search can be bookmarked;
 * a flag can be addressed from its result.
 */

const PAGE = 50;

export function FlagsPage() {
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => searchParamsToQuery(params), [params]);
  const navigate = useNavigate();
  const toast = useToast();
  const store = useFlagStore();
  useFlagStoreVersion();
  const types = store.types();

  const [items, setItems] = useState<FlagListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<{ item: FlagListItem; rect: DOMRect } | null>(null);
  const [options, setOptions] = useState<{ projects: Option[]; series: Option[]; people: Option[] }>({ projects: [], series: [], people: [] });

  useEffect(() => {
    const get = (path: string) => apiGet<Option[]>(path).catch(() => [] as Option[]);
    void Promise.all([get('/scopes'), get('/meeting-series'), get('/people')])
      .then(([projects, series, people]) => setOptions({ projects, series, people }));
  }, []);

  const key = params.toString();
  useEffect(() => {
    let live = true;
    setLoading(true);
    listFlags({ ...query, limit: PAGE, offset: 0 })
      .then((r) => { if (live) { store.seed(r.items); setItems(r.items); setTotal(r.total); } })
      .catch(() => { if (live) toast.error('Could not load flags. Try again.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  // A flag addressed here (or anywhere) updates its row in place.
  const shown = items.map((i) => {
    const st = store.get(i.id);
    return st ? { ...i, status: st.status, reply: st.reply, addressed_at: st.addressed_at, task_id: st.task_id } : i;
  });

  const loadMore = async () => {
    try {
      const r = await listFlags({ ...query, limit: PAGE, offset: items.length });
      store.seed(r.items);
      setItems((prev) => [...prev, ...r.items]);
      setTotal(r.total);
    } catch {
      toast.error('Could not load more flags. Try again.');
    }
  };

  const change = (next: FindQuery) => setParams(queryToSearchParams(next), { replace: true });
  const name = (list: Option[], id?: string) => list.find((o) => o.id === id)?.name;
  const sentence = describeQuery(query, {
    project: name(options.projects, query.projectId), series: name(options.series, query.seriesId), person: name(options.people, query.personId),
  }, types);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="m-0 flex items-center gap-2 text-xl font-semibold text-text-primary"><Flag size={20} aria-hidden />Flags</h1>
        <button type="button" onClick={() => navigate('/settings#flags')}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary">
          <Settings2 size={15} aria-hidden />Edit flag types
        </button>
      </header>
      <div className="grid items-start gap-4 lg:grid-cols-[270px_minmax(0,1fr)]">
        <aside className="rounded-lg border border-border bg-bg-card p-3.5">
          <FlagFilters value={query} onChange={change} types={types} {...options} />
        </aside>
        <section className="min-w-0 rounded-lg border border-border bg-bg-card p-4">
          <p className="m-0 text-base text-text-primary" data-testid="flag-sentence">
            {sentence.map((part, i) => (part.strong ? <b key={i} className="font-semibold text-accent-hover">{part.text}</b> : <span key={i}>{part.text}</span>))}
          </p>
          <p className="mb-2 mt-0.5 text-xs text-text-muted tabular-nums">
            {loading ? 'Loading...' : `${total} ${total === 1 ? 'result' : 'results'}`}
          </p>
          {!loading && (
            <FlagList items={shown} group={query.group} showSource types={types}
              onOpen={(item, rect) => setOpen({ item, rect })}
              emptyText="Nothing matches. Try another type or status, or a wider date range." />
          )}
          {!loading && items.length < total && (
            <button type="button" onClick={loadMore} className="mt-3 rounded-md border border-border-hover px-3 py-1 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary">
              Load more
            </button>
          )}
        </section>
      </div>
      {open && (
        <FlagPopover
          key={open.item.id}
          flagId={open.item.id}
          type={open.item.type}
          rect={open.rect}
          where="list"
          text={open.item.text}
          sourceLine={sourceLine(open.item)}
          personName={open.item.person_name}
          onOpenInContext={() => navigate(sourceUrl(open.item))}
          onOpenTask={(taskId) => navigate(`/tasks/${encodeURIComponent(taskId)}`)}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
